<template>
  <div>
    <AutocompleteLocation
      ref="addressField"
      v-model="input"
      :placeholder="$t('valuation.labels.address')"
      :country="countryCode"
      :preventSuggestions="false"
      name="address"
      :debug="true"
      @suggestion-selected="handleSuggestionSelected"
      @on-input="handleInputChange"
    />
    <div
      v-if="incompleteAddress"
      class="text-caption mb-4"
    >
      {{ incompleteAddress }}
    </div>
  </div>
</template>

<script setup>
import * as Sentry from '@sentry/vue'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import AutocompleteLocation from '@/components/Autocomplete.vue'

const { t } = useI18n()

const props = defineProps({
  countryCode: {
    type: String,
    required: true,
  },
  value: {
    type: Object,
    default: null,
  },
})

const emit = defineEmits(['input'])

const getInitialAddress = () => ({
  zip: '',
  city: '',
  street: '',
  houseNumber: '',
  countryCode: props.countryCode || 'de',
})

const addressField = ref(null)
const model = ref(null)
const input = ref('')
const incompleteAddress = ref(false)
const fieldRules = ref([])

const rules = computed(() => {
  return [
    () => !incompleteAddress.value || incompleteAddress.value,
    () => model.value !== null || t('valuation.address-field.rules.invalid'),
  ]
})

const handleInputChange = (event) => {
  const inputValue = event.value || ''
  if (!inputValue) {
    model.value = null
    input.value = ''
    incompleteAddress.value = false
    emit('input', model.value)
  }
}

const validateFieldInput = async () => {
  fieldRules.value = rules.value
  ;(await addressField.value?.$el.querySelector('input').validate?.()) || true
}

const handleSuggestionSelected = async (suggestion) => {
  incompleteAddress.value = null
  fieldRules.value = []

  if (!suggestion) {
    model.value = null
    input.value = ''
    return
  }

  input.value = suggestion.display_name

  if (!suggestion.lat || !suggestion.lon) {
    incompleteAddress.value = t('valuation.address-field.rules.incomplete', {
      field: t('labels.coordinates'),
    })
    logError('Missing coordinates for place')
    await validateFieldInput()
    return
  }

  // Map address components from Google Places format
  const address = mapGoogleAddress(suggestion)

  if (!address.zip) {
    incompleteAddress.value = t('valuation.address-field.rules.incomplete', {
      field: t('labels.zip'),
    })
    logError('Missing "zip" for place')
    await validateFieldInput()
    return
  }

  if (!address.city) {
    incompleteAddress.value = t('valuation.address-field.rules.incomplete', {
      field: t('labels.city'),
    })
    logError('Missing "city" for place')
    await validateFieldInput()
    return
  }

  // Check if street is missing or is a postal code area (not a real street)
  const postalCodeAreaPattern = /Postal Code Area \(\d+\)/i
  if (!address.street || postalCodeAreaPattern.test(address.street)) {
    incompleteAddress.value = t('alerts.valuation.incomplete-address')
    logError('Missing "street" for place')
    await validateFieldInput()
    return
  }

  model.value = {
    address,
    coordinates: { lat: parseFloat(suggestion.lat), lng: parseFloat(suggestion.lon) },
  }
}

// Map Google Places address components to the required format
const mapGoogleAddress = (suggestion) => {
  const address = getInitialAddress()

  if (suggestion.address) {
    // Extract postal code/zip
    address.zip = suggestion.address.postcode || ''

    // Extract city
    address.city = suggestion.address.city || ''

    // Extract street name
    address.street = suggestion.address.road || ''

    // Extract house number
    address.houseNumber = suggestion.address.house_number || ''

    // Extract country code
    address.countryCode = (
      suggestion.address.country_code ||
      props.countryCode ||
      'de'
    ).toLowerCase()
  }

  // If we have a structured_formatting and missing street or house number,
  // attempt to parse it from main_text
  if (
    suggestion.structured_formatting &&
    suggestion.structured_formatting.main_text &&
    (!address.street || !address.houseNumber)
  ) {
    const mainText = suggestion.structured_formatting.main_text
    const match = mainText.match(/^(.+)\s+(\d+[a-z]*)$/i)

    if (match) {
      // If we can extract street and number
      if (!address.street) address.street = match[1].trim()
      if (!address.houseNumber) address.houseNumber = match[2].trim()
    } else if (!address.street) {
      // If no match but street is missing, use the whole text
      address.street = mainText
    }
  }

  return address
}

const reset = () => {
  input.value = ''
  model.value = null
  incompleteAddress.value = false
}

const logError = (errorMessage) => {
  Sentry.addBreadcrumb({
    category: 'google-places-api',
    description: 'Invalid response from Google Places API',
  })
  Sentry.captureException(new Error(errorMessage))
}

watch(model, (value) => {
  if (value) {
    emit('input', model.value)
  }
})

watch(
  () => props.value,
  (value) => {
    if (!value) {
      reset()
    }
  }
)
defineExpose({
  reset,
  validateFieldInput,
})
</script>
